import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);

export const getTimeStamp = () => dayjs().format(`YYYYMMDDHH:mm:ss`);
export const getTimeStampForFileName = () => {
    const randomMinutes = Math.floor(Math.random() * 20) + 1; // adding random minutes to avoid duplicate file names
    const actualMinutes = dayjs().diff(dayjs().startOf('day'), 'minutes');
    const totalMinutes = actualMinutes + randomMinutes;
    return dayjs().format(`D MMM YYYY`) + `(${totalMinutes})`; // returns 17 Jan 2025 (1309)
};

/**
 * Adds a human-readable timestamp to filename (e.g., "file - 16 Dec 2025(834).pdf")
 */
export const addTimeStampForFileName = (fileName: string) => {
    const fileExtension = fileName.split('.').pop();
    const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
    return `${fileNameWithoutExtension} - ${getTimeStampForFileName()}.${fileExtension}`;
};

export const getMonthsByYear = (numOfMonths: number) => {
    const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

    let endDate = dayjs();
    let endDateMonth = endDate.month();
    let endDateYear = endDate.year();
    let startDate = dayjs().subtract(numOfMonths, 'months');
    let startDateYear = startDate.year();
    let datesObj: any = {};
    for (let i = startDateYear; i <= endDateYear; i++) {
        let monthsArr;
        if (i === endDateYear) monthsArr = months.filter((month) => parseInt(month) <= endDateMonth);
        else monthsArr = months;
        datesObj[i] = monthsArr;
    }
    return { months, datesObj, startDate, endDate };
};

export const monthsEnum: { [x: string]: { name: string; fullName: string } } = {
    '01': {
        name: 'Jan',
        fullName: 'January',
    },
    '02': {
        name: 'Feb',
        fullName: 'February',
    },
    '03': {
        name: 'Mar',
        fullName: 'March',
    },
    '04': {
        name: 'Apr',
        fullName: 'April',
    },
    '05': {
        name: 'May',
        fullName: 'May',
    },
    '06': {
        name: 'Jun',
        fullName: 'June',
    },
    '07': {
        name: 'Jul',
        fullName: 'July',
    },
    '08': {
        name: 'Aug',
        fullName: 'August',
    },
    '09': {
        name: 'Sep',
        fullName: 'September',
    },
    '10': {
        name: 'Oct',
        fullName: 'October',
    },
    '11': {
        name: 'Nov',
        fullName: 'November',
    },
    '12': {
        name: 'Dec',
        fullName: 'December',
    },
};

export const DATE_FORMATS = {
    ISO: 'YYYY-MM-DD',
    MONTH_FULL_YEAR: 'MMMM YYYY',
    DATE_FORMAT_DDMMYYYY: 'DD-MM-YYYY',
    DATE_FORMAT_D_MMM_YYYY: 'D MMM YYYY',
} as const;

export function getFormattedDate(date: string, periodSelectorVariant: string | null, isFinancials = false) {
    //USED IN DATEPICKER
    if (!date || !periodSelectorVariant) return '-';

    const dateTime = dayjs(date);
    if (isFinancials) return dateTime.format('YYYY');

    switch (periodSelectorVariant) {
        case 'Monthly':
            return dateTime.format("MMM'YY");
        case 'Quarterly': {
            let fyQuarter = Math.ceil((parseInt(dateTime.format('MM')) - 3) / 3);
            fyQuarter = fyQuarter <= 0 ? 4 : fyQuarter;
            return `Q${fyQuarter}'FY${parseInt(dateTime.add(parseInt(dateTime.format('MM')) > 3 ? 1 : 0, 'years').format('YY'))}`;
        }
        case 'Yearly':
            return dateTime.format('YYYY');
        default:
            return dateTime.format("MMM'YY");
    }
}

export function initializeDatepickerDates(periodSelectorVariant: string | null) {
    const DATE_FORMAT = 'YYYY-MM-DD';
    if (!periodSelectorVariant) return [];
    //Initializing dates for non recurring metrics. (Graphs in Section 1 of Dashboard)
    const now = dayjs();
    // const now = dayjs('2023-04-01');
    const previousMonthLastDate = now.subtract(1, 'month').endOf('month');
    const monthlyStartDate = previousMonthLastDate.subtract(11, 'months').startOf('month');

    const currentQuarterStart = now.startOf('quarter');
    const previousQuarterEnd = currentQuarterStart.subtract(1, 'day');
    const quarterlyStartDate = currentQuarterStart.subtract(3, 'quarters').startOf('quarter');

    const currentFinancialYearStart =
        now.month() < 3
            ? now
                  .year(now.year() - 1)
                  .startOf('year')
                  .add(3, 'month')
            : now.startOf('year').add(3, 'month');
    const previousFinancialYearStart = currentFinancialYearStart.subtract(1, 'year');
    const previousFinancialYearEnd = currentFinancialYearStart.subtract(1, 'day');
    // const yearlyStartDate = previousFinancialYearEnd.subtract(1, 'years').startOf('month');

    let toDate = '';
    let fromDate = '';
    switch (periodSelectorVariant) {
        case 'Monthly':
            toDate = previousMonthLastDate.format(DATE_FORMAT);
            fromDate = monthlyStartDate.format(DATE_FORMAT);
            break;
        case 'Quarterly':
            toDate = now.format(DATE_FORMAT);
            fromDate = quarterlyStartDate.format(DATE_FORMAT);
            break;
        case 'Yearly':
            toDate = now.format(DATE_FORMAT);
            fromDate = previousFinancialYearStart.format(DATE_FORMAT);
            break;
    }

    return [fromDate, toDate, previousMonthLastDate.format(DATE_FORMAT)];
}

export const getFinancialHeadersForDebtSchedule = (companyFinancialsDataUpdateDate) => {
    const provisionalDataHeader = `Provisional${companyFinancialsDataUpdateDate ? ` (${companyFinancialsDataUpdateDate})*` : '*'}`;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    let startYear;
    startYear = currentYear - 3;

    // from january to march, we need to go one more year back
    if (currentMonth <= 3) {
        startYear = currentYear - 4;
    }

    return [
        {
            name: 'Particulars (Cr)',
            key: 'classificationName',
        },
        {
            name: `FY${String(startYear + 1).slice(-2)}A`,
            key: String(startYear),
        },
        {
            name: `FY${String(startYear + 2).slice(-2)}A`,
            key: String(startYear + 1),
        },
        {
            name: `FY${String(startYear + 3).slice(-2)}A`,
            key: String(startYear + 2),
        },
        {
            name: provisionalDataHeader,
            key: 'provisional',
        },
    ];
};

export const getFinancialYear = (year: number, withPrefix = false, returnEndYear = false) => {
    const suffix = getFiscalYearSuffix(year, returnEndYear);
    return withPrefix ? `FY’${suffix}` : suffix;
};

export const getFiscalYearSuffix = (year, returnEndYear = false) => {
    if (!year || isNaN(year)) return null;

    const startYear = new Date(year, 0).getFullYear();
    const endYear = startYear + 1;
    if (returnEndYear) return endYear;
    return `${startYear.toString().slice(-2)}-${endYear.toString().slice(-2)}`;
};

export const getStartDateEndDate = () => {
    const now = dayjs();
    const currentMonth = now.month();
    const currentMonthBetweenAprilAndSeptember = currentMonth >= 3 && currentMonth <= 8;
    let startDate: string, endDate: string;

    if (currentMonthBetweenAprilAndSeptember) {
        endDate = now
            .month(2)
            .date(31)
            .year(now.year() - 1)
            .format('YYYY-MM-DD');
        startDate = now
            .month(3)
            .date(1)
            .year(now.year() - 7)
            .format('YYYY-MM-DD');
    } else {
        endDate = now.month(2).date(31).year(now.year()).format('YYYY-MM-DD');
        startDate = now
            .month(3)
            .date(1)
            .year(now.year() - 6)
            .format('YYYY-MM-DD');
    }
    return { startDate, endDate };
};

export const formatFinancialDate = (date: string) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    };
    return `As of ${new Intl.DateTimeFormat('en-GB', options).format(dateObj)}`;
};

export const checkIfDateIsValid = (date: Date): boolean => {
    if (!date || !(date instanceof Date)) return false;
    return dayjs(date).isValid();
};

export const formatDateString = (date: Date, displayFormat: string) => {
    if (!checkIfDateIsValid(date)) {
        return '';
    }

    if (!displayFormat || displayFormat.trim() === '') {
        return dayjs(date).format(DATE_FORMATS.ISO);
    }
    return dayjs(date).format(displayFormat);
};

export const getAuditedYear = (year: number) => {
    if (!year || isNaN(year)) return '';
    const cutoff = dayjs(`${year + 1}-03-31`);
    return dayjs().isAfter(cutoff) ? `${year + 1}` : `${year}`;
};

/**
 * Formats a timestamp (number) or date string using the specified format
 * @param timestamp - Timestamp in milliseconds (number) or date string
 * @param format - Date format string (e.g., 'DD MMM YYYY'). Defaults to ISO format if not provided
 * @returns Formatted date string or empty string if invalid
 */
export const formatDateByFormat = (timestamp: number | string | undefined, format?: string): string => {
    if (!timestamp) return '';
    const date = dayjs(timestamp);
    if (!date.isValid()) return '';
    return date.format(format || DATE_FORMATS.ISO);
};
